<template>
  <v-card>
    <Loader
      v-if="loading"
      :key="generateUniqueId()"
      :loader-state="loading"
    ></Loader>

    <v-card-title class="float-left">
      Détails d'une activité : {{ activity ? activity.translations.fr.name : '-' }}
    </v-card-title>

    <LocalSwitcher
      @updateCurrentLocale="updateCurrentLocale($event)"
    ></LocalSwitcher>

    <v-card-text>
      <v-form
        v-model="valid"
        class="multi-col-validation mt-5"
        @submit.prevent="checkForm"
      >
        <p v-if="errors.length">
          <v-alert
            v-for="(error, index) in errors"
            :key="index"
            text
            dense
            type="error"
          >
            {{ error.title }}
          </v-alert>
        </p>

        <Snackbar
          v-if="successMessage"
          :key="generateUniqueId()"
          :snackbar-state="snackbar"
          :success-message="successMessage"
        ></Snackbar>

        <v-row>
          <v-col
            cols="12"
            md="12"
          >
            <v-card flat>
              <v-card-text>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <h4>Informations de l'activité SEO</h4>
                    <br>
                    <v-text-field
                      v-model="name[currentLocale]"
                      label="Nom"
                      class="mb-5"
                      outlined
                      dense
                      placeholder="Nom"
                      hide-details
                    >
                      <template v-slot:append>
                        <country-flag
                          class="inner-input-flag"
                          :country="currentLocale === 'en' ? 'gb' : currentLocale"
                          size="normal"
                          shadow
                        />
                      </template>
                    </v-text-field>

                    <v-text-field
                      v-model="slug[currentLocale]"
                      label="Slug"
                      class="mb-5"
                      outlined
                      dense
                      placeholder="Slug"
                      hide-details
                    >
                      <template v-slot:append>
                        <country-flag
                          class="inner-input-flag"
                          :country="currentLocale === 'en' ? 'gb' : currentLocale"
                          size="normal"
                          shadow
                        />
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <h4>Contenu dynamique</h4>
                    <br>
                    <div class="content-ckeditor-region">
                      <ckeditor
                        v-model="dynamicDescription[currentLocale]"
                        :editor="editor"
                        :config="editorConfig"
                        height="10"
                        row="50"
                      />
                      <country-flag
                        class="inner-input-flag content-flag-placeholder"
                        :country="currentLocale === 'en' ? 'gb' : currentLocale"
                        size="normal"
                        shadow
                      />
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    md="12"
                  >
                    <h4>Sélection de services</h4>
                    <br>
                    <v-select
                      v-model="serviceSelection"
                      :items="processedServices"
                      multiple
                      outlined
                      chips
                      hint="Choisir les services à associer à cette activité"
                      persistent-hint
                      label="Services"
                      item-text="text"
                      item-value="value"
                      autocomplete="off"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-btn
              class="mt-5 mr-2"
              color="primary"
              :disabled="!valid"
              type="submit"
            >
              Valider
            </v-btn>

            <v-btn
              class="mt-5"
              text
              :to="{ name: 'activityList' }"
            >
              Retour
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { mdiEyeOutline, mdiEyeOffOutline, mdiCameraOutline, mdiMinusBox, mdiPencil, mdiCompassRose, mdiThumbUp, mdiSkiWater } from '@mdi/js'
import config from '../../../config'
import LocalSwitcher from '@/components/Common/LocalSwitcher'
import Snackbar from '@/components/Common/Snackbar'
import Loader from '@/components/Common/Loader'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import FullEditor from 'ckeditor5-custom-build/build/ckeditor'

export default {
  components: {
    Snackbar,
    LocalSwitcher,
    Loader,
    ckeditor: CKEditor.component
  },
  data () {
    return {
      valid: false,
      errors: [],
      snackbar: false,
      successMessage: null,
      loading: false,
      currentLocale: 'fr',
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
        mdiCameraOutline,
        mdiMinusBox,
        mdiPencil,
        mdiCompassRose,
        mdiSkiWater,
        mdiThumbUp
      },
      name: [],
      slug: [],
      dynamicDescription: [],
      activity: null,
      entityData: {
        id: null,
        currentLocale: 'fr',
        defaultLocale: 'fr',
        newTranslations: {}
      },
      services: [],
      serviceSelection: [],
      editor: FullEditor,
      editorConfig: {
        toolbar: {
          shouldNotGroupWhenFull: true
        },
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        htmlSupport: {
          allow: [
            {
              name: /.*/,
              attributes: true,
              classes: true,
              styles: true
            }
          ]
        },
        simpleUpload: {
          uploadUrl: config.apiUrl + '/api/medias',
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          }
        }
      }
    }
  },
  computed: {
    processedServices () {
      return this.services.map(service => ({
        text: service.translations && service.translations[this.currentLocale] 
          ? service.translations[this.currentLocale].value 
          : (service.label && service.label.translations && service.label.translations[this.currentLocale]
              ? service.label.translations[this.currentLocale].value
              : 'Service sans nom'),
        value: service.flowerDataId
      })).sort((a, b) => a.text.localeCompare(b.text))
    }
  },
  created () {
    this.getActivity()
    this.fetchServices()
  },
  methods: {
    checkForm () {
      this.errors = []

      if (this.valid) {
        this.putActivity()
      }
    },
    apiUrl () {
      return config.apiUrl
    },
    getActivity () {
      const activityId = this.$route.params.activityId

      this.loading = true

      Vue.prototype.$http
        .get(config.apiUrl + '/api/seo/activities/' + activityId, {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          }
        })
        .then(response => {
          if (response.status === 200) {
            this.activity = response.data
            this.entityData.id = this.activity.id

            const translations = this.activity.translations

            for (const [currentKey, currentValue] of Object.entries(translations)) {
              this.name[currentKey] = currentValue.name
              this.slug[currentKey] = currentValue.slug
              this.dynamicDescription[currentKey] = currentValue.dynamicDescription
            }

            // Initialiser les services sélectionnés
            if (this.activity.children && this.activity.children.length > 0) {
              this.serviceSelection = this.activity.children.map(id => parseInt(id))
            }
          }

          this.loading = false
        })
        .catch(e => {
          const response = e.response

          if (response.status === 400 || response.status === 404) {
            const data = response.data
            this.errors = data.violations
          }

          this.valid = true
          this.loading = false
        })
    },
    putActivity () {
      this.loading = true
      this.valid = false
      this.successMessage = null

      this.handleTranslations([
        'name',
        'slug',
        'dynamicDescription'
      ])

      // Ajouter les services sélectionnés
      this.entityData.children = this.serviceSelection

      Vue.prototype.$http
        .put(config.apiUrl + '/api/seo/activities/' + this.entityData.id, this.entityData, {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          }
        })
        .then(response => {
          if (response.status === 200) {
            this.activity = response.data

            this.errors = []
            this.snackbar = true
            this.successMessage = 'Activité SEO mise à jour avec succès.'
          }

          this.valid = true
          this.loading = false
        })
        .catch(e => {
          const response = e.response

          this.successMessage = null

          if (response.status === 400 || response.status === 404) {
            const data = response.data
            this.errors = data.violations
          }

          this.valid = true
          this.loading = false
        })
    },
    handleTranslations (attributes) {
      for (let i = 0; i < attributes.length; i++) {
        const currentAttribute = attributes[i]

        for (const [currentKey, currentValue] of Object.entries(this[currentAttribute])) {
          let translation = {
            locale: currentKey,
            empty: false
          }

          translation[currentAttribute] = currentValue

          const currentObjState = this.entityData.newTranslations[currentKey]

          this.entityData.newTranslations[currentKey] = {...currentObjState, ...translation}
        }
      }
    },
    updateCurrentLocale (locale) {
      this.currentLocale = locale
      this.successMessage = null
    },
    fetchServices () {
      Vue.prototype.$http
        .get(config.apiUrl + '/api/services', {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken
          }
        })
        .then(response => {
          this.services = response.data
        })
        .catch(e => {
          console.error('Error fetching services:', e)
        })
    }
  }
}
</script> 